import { Link } from '@tanstack/react-router';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { formatLocalYmd } from '../../../lib/date-utils.js';
import { kindLabel } from '../../../lib/project-feedback-view.js';
import type { ProjectFeedbackSummary } from '../../../types/project-feedback.js';

/** @graph-connects none */
export interface FeedbackListProps {
  /** 表示する FB */
  feedbacks: ProjectFeedbackSummary[];
  /** メンバー列を出すか（評価者向け） */
  showMember?: boolean;
  /** 0 件のときの文言 */
  emptyText: string;
}

/**
 * 出ている異議の数。0 件なら何も出さない。
 * @graph-connects none
 */
function ObjectionCount({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span
      className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[var(--color-bg-hover)] px-1.5 text-xs font-semibold text-[var(--color-text-primary)]"
      aria-label={`異議 ${String(count)} 件`}
    >
      {count}
    </span>
  );
}

/**
 * 案件 FB の一覧（デスクトップは表、モバイルはカード）。行から詳細へ遷移する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business List project feedback rows (project, kind, generated date, open objections, optionally member) linking to the detail page
 * @graph-connects kindLabel [calls] FB の種類の表示名
 * @graph-connects ObjectionCount [calls] 異議の数
 */
export function FeedbackList({ feedbacks, showMember = false, emptyText }: FeedbackListProps) {
  if (feedbacks.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">{emptyText}</Card>
    );
  }

  return (
    <>
      <Card className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-secondary)]">
              {showMember && <th className="px-4 py-3 font-medium">メンバー</th>}
              <th className="px-4 py-3 font-medium">案件</th>
              <th className="px-4 py-3 font-medium">種類</th>
              <th className="px-4 py-3 font-medium">生成日</th>
              <th className="px-4 py-3 text-center font-medium">異議</th>
            </tr>
          </thead>
          <tbody>
            {feedbacks.map((feedback) => (
              <tr
                key={feedback.feedbackId}
                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
              >
                {showMember && <td className="px-4 py-3">{feedback.nickname}</td>}
                <td className="px-4 py-3">
                  <Link
                    to="/project-feedbacks/$feedbackId"
                    params={{ feedbackId: feedback.feedbackId }}
                    className="block"
                  >
                    <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                      {feedback.pjKey}
                    </span>
                    <span className="ml-2 text-[var(--color-text-primary)]">
                      {feedback.projectTitle}
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-3 text-xs">{kindLabel(feedback.kind)}</td>
                <td className="px-4 py-3 text-xs text-[var(--color-text-secondary)]">
                  {formatLocalYmd(feedback.generatedAt)}
                </td>
                <td className="px-4 py-3 text-center">
                  <ObjectionCount count={feedback.openObjectionCount} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ul className="space-y-3 md:hidden">
        {feedbacks.map((feedback) => (
          <li key={feedback.feedbackId}>
            <Link to="/project-feedbacks/$feedbackId" params={{ feedbackId: feedback.feedbackId }}>
              <Card className="p-4 hover:bg-[var(--color-bg-hover)]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                    {feedback.pjKey}
                  </span>
                  <ObjectionCount count={feedback.openObjectionCount} />
                </div>
                <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                  {feedback.projectTitle}
                </p>
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                  {showMember && `${feedback.nickname} · `}
                  {kindLabel(feedback.kind)} · {formatLocalYmd(feedback.generatedAt)}
                </p>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
